type Axis = 'x' | 'y';

type Bars = Record<Axis, {
    bar: HTMLElement;
    thumb: HTMLElement;
    dragging: boolean;
    startPos: number;
    startScroll: number;
}>;

export class CustomScrollbar {
    private readonly wrapper: HTMLElement;
    private readonly img: HTMLElement;
    private scroller: HTMLElement;
    private scale: number = 1;

    private bars: Bars = {} as any;

    private contentDragging: boolean = false;
    private dragStartX: number = 0;
    private dragStartY: number = 0;
    private startScrollLeft: number = 0;
    private startScrollTop: number = 0;

    constructor(wrapper: HTMLElement) {
        this.wrapper = wrapper;
        this.img = wrapper.querySelector('.j-custom-scrollable-el')!;

        this.initScrollContainer();
        this.createScrollbar('x');
        this.createScrollbar('y');

        this.bindEvents();
        this.update();
    }

    public update(): void {
        this.updateAxis('x');
        this.updateAxis('y');
    }

    public setScale(scale: number): void {
        this.scale = scale;
        this.update();
    }

    public setScroll(axis: Axis, value: number): void {
        if (axis === 'x') {
            this.scroller.scrollLeft = Math.max(0, value);
        } else {
            this.scroller.scrollTop = Math.max(0, value);
        }
    }

    public scrollToElement(element: HTMLElement, options?: { offsetX?: number, offsetY?: number }): void {
        const offsetX = options?.offsetX || 0;
        const offsetY = options?.offsetY || 0;

        // Получаем позицию элемента относительно контейнера с контентом (img)
        const imgRect = this.img.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();

        // Вычисляем позицию элемента внутри изображения (без учета скролла)
        const elementLeftInImg = elementRect.left - imgRect.left;
        const elementTopInImg = elementRect.top - imgRect.top;

        // Вычисляем абсолютную позицию с учетом текущего скролла
        const absoluteLeft = elementLeftInImg + this.scroller.scrollLeft;
        const absoluteTop = elementTopInImg + this.scroller.scrollTop;

        // Центрируем элемент в viewport
        const targetScrollLeft = absoluteLeft - (this.scroller.clientWidth / 2) + (elementRect.width / 2) + offsetX;
        const targetScrollTop = absoluteTop - (this.scroller.clientHeight / 2) + (elementRect.height / 2) + offsetY;

        this.scroller.scrollLeft = targetScrollLeft;
        this.scroller.scrollTop = targetScrollTop;

        this.update();
    }

    public setCenter(): void {
        const contentWidth = this.img.scrollWidth * this.scale;
        const contentHeight = this.img.scrollHeight * this.scale;
        const viewportWidth = this.scroller.clientWidth;
        const viewportHeight = this.scroller.clientHeight;

        this.scroller.scrollLeft = (contentWidth - viewportWidth) / 2;
        this.scroller.scrollTop = (contentHeight - viewportHeight) / 2;

        this.update();
    }

    /**
     * Сбрасывает позицию скролла к начальному значению (0, 0)
     */
    public resetPosition(): void {
        this.scroller.scrollLeft = 0;
        this.scroller.scrollTop = 0;
        this.update();
    }


    private initScrollContainer(): void {
        this.wrapper.classList.add('no-native-scroll');

        const scroller = document.createElement('div');

        Object.assign(scroller.style, {
            width   : '100%',
            height  : '100%',
            overflow: 'scroll',
            position: 'relative'
        });
        scroller.appendChild(this.img);
        this.wrapper.appendChild(scroller);
        this.scroller = scroller;
    }

    private createScrollbar(axis: Axis): void {
        const bar = document.createElement('div');
        const thumb = document.createElement('div');

        bar.className = `custom-scrollbar ${axis}`;
        thumb.className = 'thumb';
        bar.appendChild(thumb);
        this.wrapper.appendChild(bar);

        this.bars[axis] = {
            bar,
            thumb,
            dragging   : false,
            startPos   : 0,
            startScroll: 0
        };
    }

    private bindEvents(): void {
        this.scroller.addEventListener('scroll', () => {
            return this.update();
        });
        window.addEventListener('resize', () => {
            return this.update();
        });

        (['x', 'y'] as Axis[]).forEach((axis: Axis) => {
            // eslint-disable-next-line @typescript-eslint/typedef
            const {bar, thumb} = this.bars[axis];

            thumb.addEventListener('mousedown', (event: MouseEvent) => {
                this.startDrag(event, axis);
            });
            bar.addEventListener('click', (event: MouseEvent) => {
                this.jumpTo(event, axis);
            });
        });

        document.addEventListener('mousemove', (event: MouseEvent) => {
            this.onDrag(event);
            this.onContentDrag(event);
        });
        document.addEventListener('mouseup', () => {
            this.stopDrag();
            this.stopContentDrag();
        });

        this.img.addEventListener('mousedown', (event: MouseEvent) => {
            return this.startContentDrag(event);
        });
    }

    private startDrag(event: MouseEvent, axis: Axis): void {
        const b = this.bars[axis];

        b.dragging = true;
        b.startPos = axis === 'x' ? event.clientX : event.clientY;
        b.startScroll = axis === 'x' ? this.scroller.scrollLeft : this.scroller.scrollTop;
        event.preventDefault();
        event.stopPropagation();
    }

    private onDrag(event: MouseEvent): void {
        (['x', 'y'] as Axis[]).forEach((axis: Axis) => {
            const b = this.bars[axis];

            if (!b.dragging) {
                return;
            }

            const delta = (axis === 'x' ? event.clientX : event.clientY) - b.startPos;
            const track = axis === 'x' ? b.bar.clientWidth : b.bar.clientHeight;
            const thumb = axis === 'x' ? b.thumb.clientWidth : b.thumb.clientHeight;
            const maxScroll = this.getMaxScroll(axis);

            const ratio = delta / (track - thumb);

            this.setScroll(axis, b.startScroll + (ratio * maxScroll));
        });
    }

    private stopDrag(): void {
        Object.values(this.bars).forEach((b: { dragging: boolean }) => {
            b.dragging = false;
        });

        requestAnimationFrame(() => {
            this.update();
        });
    }

    private jumpTo(event: MouseEvent, axis: Axis): void {
        if (event.target !== this.bars[axis].bar) {
            return;
        }

        const rect = this.bars[axis].bar.getBoundingClientRect();
        const pos = axis === 'x' ? event.clientX - rect.left : event.clientY - rect.top;
        const thumb = axis === 'x' ?
            this.bars[axis].thumb.clientWidth :
            this.bars[axis].thumb.clientHeight;

        const track = axis === 'x' ?
            this.bars[axis].bar.clientWidth :
            this.bars[axis].bar.clientHeight;

        this.setScroll(axis, (pos - (thumb / 2)) / (track - thumb) * this.getMaxScroll(axis));
    }

    private startContentDrag(event: MouseEvent): void {
        if ((event.target as HTMLElement).closest('.custom-scrollbar')) {
            return;
        }

        this.contentDragging = true;
        this.dragStartX = event.clientX;
        this.dragStartY = event.clientY;
        this.startScrollLeft = this.scroller.scrollLeft;
        this.startScrollTop = this.scroller.scrollTop;

        event.preventDefault();
    }

    private onContentDrag(event: MouseEvent): void {
        if (!this.contentDragging) {
            return;
        }

        const dx = event.clientX - this.dragStartX;
        const dy = event.clientY - this.dragStartY;

        this.scroller.scrollLeft = this.startScrollLeft - dx;
        this.scroller.scrollTop = this.startScrollTop - dy;
    }

    private stopContentDrag(): void {
        if (!this.contentDragging) {
            return;
        }
        this.contentDragging = false;

        requestAnimationFrame(() => {
            this.update();
        });
    }

    private updateAxis(axis: Axis): void {
        const b = this.bars[axis];
        const viewport = axis === 'x' ? this.wrapper.clientWidth : this.wrapper.clientHeight;
        const baseContent = axis === 'x' ? this.img.scrollWidth : this.img.scrollHeight;
        const content = baseContent * this.scale;
        const scroll = axis === 'x' ? this.scroller.scrollLeft : this.scroller.scrollTop;

        b.bar.style.display = content > viewport ? 'block' : 'none';

        const track = axis === 'x' ? b.bar.clientWidth : b.bar.clientHeight;
        const thumbSize = Math.max((viewport / content) * track, 30);
        const pos = (scroll / (content - viewport)) * (track - thumbSize);

        if (axis === 'x') {
            b.thumb.style.width = `${thumbSize}px`;
            b.thumb.style.left = `${isNaN(pos) ? 0 : pos}px`;
        } else {
            b.thumb.style.height = `${thumbSize}px`;
            b.thumb.style.top = `${isNaN(pos) ? 0 : pos}px`;
        }
    }

    private getMaxScroll(axis: Axis): number {
        const baseWidth = this.img.scrollWidth;
        const baseHeight = this.img.scrollHeight;

        return axis === 'x' ?
            (baseWidth * this.scale) - this.wrapper.clientWidth :
            (baseHeight * this.scale) - this.wrapper.clientHeight;
    }
}
